import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Package, CreditCard, DollarSign, FileText, BarChart3 } from "lucide-react";

export default function AdminDashboard() {
  const { t } = useI18n();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [costs, setCosts] = useState(null);

  const fetchData = (p) => {
    setLoading(true);
    api.get(`/admin/analytics?period=${p}`).then((r) => setAnalytics(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(period); }, [period]);
  useEffect(() => { api.get("/admin/dataforseo-costs?days=30").then((r) => setCosts(r.data)).catch(() => {}); }, []);

  if (loading && !analytics) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const cards = [
    { label: "Totaal Gebruikers", value: analytics?.total_users || 0, icon: Users, color: "text-blue-500" },
    { label: "Totaal Producten", value: analytics?.total_products || 0, icon: Package, color: "text-emerald-500" },
    { label: "Actieve Abonnementen", value: analytics?.active_subscriptions || 0, icon: CreditCard, color: "text-amber-500" },
    { label: "Totaal Concurrenten", value: analytics?.total_competitors || 0, icon: BarChart3, color: "text-purple-500" },
    { label: "Totaal Facturen", value: analytics?.total_invoices || 0, icon: FileText, color: "text-orange-500" },
  ];

  const periodLabels = { all: "Alles", month: "Maand", quarter: "Kwartaal", year: "Jaar" };

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <h1 className="text-2xl font-bold">{t("admin.dashboard")}</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="bg-card border-border" data-testid={`admin-stat-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <c.icon className={`w-4 h-4 ${c.color}`} />
              </div>
              <p className="text-2xl font-bold">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* DataForSEO cost, measured per request (last 30 days) */}
      {costs && (
        <Card className="bg-card border-border" data-testid="dataforseo-cost-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">DataForSEO kosten (laatste 30 dagen)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-6 text-sm">
              <div><p className="text-muted-foreground">Totaal</p><p className="text-xl font-bold">$ {costs.total_cost.toFixed(2)}</p></div>
              <div><p className="text-muted-foreground">Requests</p><p className="text-xl font-bold">{costs.requests}</p></div>
              <div><p className="text-muted-foreground">Per product per markt per scan</p><p className="text-xl font-bold">$ {costs.cost_per_product_market.toFixed(4)}</p></div>
            </div>
            {costs.customers.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Klant</th><th>Producten</th><th>Requests</th><th className="text-right">Kost</th></tr></thead>
                <tbody>
                  {costs.customers.map((c) => (
                    <tr key={c.user_id} className="border-t border-border"><td className="py-1">{c.company || c.email}</td><td>{c.products}</td><td>{c.requests}</td><td className="text-right">$ {c.cost.toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Revenue with period filter */}
      <Card className="bg-card border-border" data-testid="revenue-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" /> Totaal Omzet
          </CardTitle>
          <div className="flex gap-1">
            {Object.entries(periodLabels).map(([key, label]) => (
              <Button key={key} variant={period === key ? "default" : "ghost"} size="sm" className={`h-7 text-xs ${period === key ? "bg-blue-600" : ""}`}
                onClick={() => setPeriod(key)} data-testid={`period-${key}`}>
                {label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-4xl font-bold font-mono-data text-emerald-500">{analytics?.total_revenue?.toFixed(2)} <span className="text-lg text-muted-foreground">EUR</span></p>
          <p className="text-xs text-muted-foreground mt-1">
            {period === "all" ? "Totaal sinds opstart" : period === "month" ? "Laatste 30 dagen" : period === "quarter" ? "Laatste 90 dagen" : "Laatste 12 maanden"}
          </p>
        </CardContent>
      </Card>

      {analytics?.plan_breakdown && Object.keys(analytics.plan_breakdown).length > 0 && (
        <Card className="bg-card border-border" data-testid="plan-breakdown-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Abonnement Verdeling</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(analytics.plan_breakdown).map(([plan, count]) => (
                <Badge key={plan} variant="secondary" className="text-sm py-1.5 px-3">{plan}: {count}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
